import { useMemo, useRef, useEffect } from 'react';

import { memoGeneric } from '@/js/libs/memo/index.ts';

import Icons from '../../../../Elements/Icons/index.ts';
import { isEqualOptions } from '../../helpers.ts';
import type { FormatOptionLabel, Option } from '../../types.ts';
import { OptionWrapper, CheckmarkWrapper } from './styled.ts';

interface OptionProps<T> {
  option: Option<T>;
  selected: Option<T> | null;
  isFocused?: boolean;
  onClick?: () => void;
  dataTestId?: string;
  optionSelected?: (optionA: Option<T>, optionB: Option<T>) => boolean;
  formatOptionLabel?: FormatOptionLabel<T>;
}

export const OptionSelect = memoGeneric(function OptionSelect<T>({
  option,
  selected,
  isFocused = false,
  onClick,
  optionSelected,
  formatOptionLabel,
}: OptionProps<T>) {
  const isSelected = useMemo(() => {
    if (!selected) {
      return false;
    }
    return optionSelected ? optionSelected(selected, option) : isEqualOptions(selected, option);
  }, [selected, option, optionSelected]);

  const label = useMemo(() => (formatOptionLabel ? formatOptionLabel(option) : option.label), [formatOptionLabel, option]);
  const ref = useRef<HTMLDivElement>(null);

  // Scroll into view when focused
  useEffect(() => {
    if (isFocused && ref.current) {
      ref.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [isFocused]);

  return (
    <OptionWrapper onClick={onClick} $isSelected={isSelected} $isFocused={isFocused} ref={ref} data-testid='option-label'>
      <span>{label}</span>
      {isSelected && (
        <CheckmarkWrapper>
          <Icons.Checkmark size={20} />
        </CheckmarkWrapper>
      )}
    </OptionWrapper>
  );
});
